import type {
  CsmMessageRequest,
  DismissFaqSearchMissRequest,
  DismissFaqSearchMissResult,
  FaqAdminTree,
  FaqCategory,
  FaqPublic,
  FaqQuestion,
  FaqSearchMissList,
  LicenceDetail,
  RecordFaqSearchMissRequest,
  ReorderFaqRequest,
  RestoreFaqSearchMissRequest,
} from '@shared/schemas';

import api from '@/shared/api/api-client';

import type { FaqGateway } from './faq.gateway';

/**
 * Real implementation over the shared api client: the single place that knows the
 * FAQ endpoints (`faq.routes.ts`) and request shapes. `api.*` unwrap the
 * `{ success, data }` envelope; a 204 resolves to `undefined`.
 */
export const httpFaqGateway: FaqGateway = {
  loadPublicFaq: () => api.get<FaqPublic>('/faq'),
  loadAdminFaq: () => api.get<FaqAdminTree>('/admin/faq'),

  createCategory: (payload) => api.post<FaqCategory>('/admin/faq/categories', payload),
  updateCategory: (id, payload) => api.patch<FaqCategory>(`/admin/faq/categories/${id}`, payload),
  deleteCategory: async (id) => {
    await api.delete(`/admin/faq/categories/${id}`);
  },
  restoreCategory: (id) => api.post<FaqCategory>(`/admin/faq/categories/${id}/restore`),
  reorderCategories: async (orderedIds) => {
    const body: ReorderFaqRequest = { ordered_ids: orderedIds };
    await api.put('/admin/faq/categories/order', body);
  },

  createQuestion: (payload) => api.post<FaqQuestion>('/admin/faq/questions', payload),
  updateQuestion: (id, payload) => api.patch<FaqQuestion>(`/admin/faq/questions/${id}`, payload),
  deleteQuestion: async (id) => {
    await api.delete(`/admin/faq/questions/${id}`);
  },
  restoreQuestion: (id) => api.post<FaqQuestion>(`/admin/faq/questions/${id}/restore`),
  moveQuestion: (id, payload) => api.post<FaqQuestion>(`/admin/faq/questions/${id}/move`, payload),
  reorderQuestions: async (categoryId, orderedIds) => {
    const body: ReorderFaqRequest = { ordered_ids: orderedIds };
    await api.put(`/admin/faq/categories/${categoryId}/questions/order`, body);
  },

  loadCurrentCsm: async () => {
    const { csm } = await api.get<LicenceDetail>('/licences/current');
    return csm && { first_name: csm.first_name, last_name: csm.last_name };
  },
  sendCsmMessage: async (message) => {
    const body: CsmMessageRequest = { message };
    await api.post<void>('/licences/current/csm-message', body);
  },

  recordSearchMiss: async (query) => {
    const body: RecordFaqSearchMissRequest = { query };
    await api.post<void>('/faq/search-misses', body);
  },
  loadSearchMisses: () => api.get<FaqSearchMissList>('/admin/faq/search-misses'),
  dismissSearchMiss: (normalizedQuery) => {
    const body: DismissFaqSearchMissRequest = { normalized_query: normalizedQuery };
    return api.post<DismissFaqSearchMissResult>('/admin/faq/search-misses/dismiss', body);
  },
  restoreSearchMiss: async (normalizedQuery, dismissedAt) => {
    const body: RestoreFaqSearchMissRequest = {
      normalized_query: normalizedQuery,
      dismissed_at: dismissedAt,
    };
    await api.post<void>('/admin/faq/search-misses/restore', body);
  },
};
